import React, { useMemo, useState } from 'react';
import { SearchBox, Spinner, SpinnerSize, Toggle } from '@fluentui/react';
import ReactECharts from 'echarts-for-react';
import { CalendarCheckmarkRegular, PeopleRegular, TaskListSquareLtrRegular, ArrowTrendingRegular } from '@fluentui/react-icons';

import * as Functions from './VisitExecution.Functions';
import { STAGE_LABEL, statusLabel } from './PatientStatusStatistics.Functions';
import ExcelExportButton from './ExcelExportButton.Component';
import { buildFileName, downloadWorkbook, visitExecutionSheets } from './StatisticsExcel.Functions';

import './ContractStatistics.Styles.css';
import './PatientStatusStatistics.Styles.css';
import './VisitExecution.Styles.css';

export type VisitExecutionSource = 'contract' | 'emr';

interface IProps {
    model: Functions.VisitExecutionModel;
    /** De dónde sale el plan: cambia los textos, no la pantalla. */
    source: VisitExecutionSource;
    isLoading?: boolean;
    /** Texto para el nombre del archivo Excel. */
    fileBase?: string;
    /** Alcance que se escribe dentro del Excel ("Protocolo P-12"). */
    exportScope?: string;
}

const TEXTS: Record<VisitExecutionSource, {
    description: string;
    empty: string;
    planDesc: string;
    expectedDesc: (required: number, randomized: number) => string;
    timelineBars: string;
}> = {
    contract: {
        description:
            'Avance del plan de visitas del contrato. Una visita cuenta como realizada cuando el paciente tiene al menos un consumo cargado en ella, con la fecha de realización del primero.',
        empty: 'El contrato no tiene visitas configuradas.',
        planDesc: 'Plan de visitas del contrato, para cada paciente',
        expectedDesc: (required, randomized) => `Visitas obligatorias × pacientes randomizados (${required} × ${randomized})`,
        timelineBars: 'Visitas realizadas en el mes'
    },
    emr: {
        description:
            'Avance del plan de visitas del protocolo según el registro clínico, sin contrato asociado. Una visita cuenta como realizada cuando está Completada, Modificada o Auto completada.',
        empty: 'El protocolo no tiene un plan de visitas cargado.',
        planDesc: 'Plan de visitas del protocolo, para cada paciente',
        expectedDesc: (required, randomized) => `Visitas del plan × pacientes randomizados (${required} × ${randomized})`,
        timelineBars: 'Visitas completadas en el mes'
    }
};

const CELL_TITLE: Record<Functions.VisitCellState, string> = {
    done: 'realizada',
    started: 'iniciada',
    skipped: 'salteada',
    failed: 'falla de selección'
};

const CELL_SYMBOL: Record<Functions.VisitCellState, string> = {
    done: '✓',
    started: '◐',
    skipped: '↷',
    failed: '✕'
};

/**
 * Ejecución de visitas: cuánto del plan ya se hizo, cómo avanzó en el tiempo y, a
 * pedido, el detalle paciente por paciente. Dibuja un modelo ya calculado; de qué
 * fuente sale —contrato o registro clínico— lo decide quien lo arma.
 */
export const VisitExecutionComponent: React.FC<IProps> = ({ model, source, isLoading, fileBase, exportScope }) => {
    const [isMatrixOpen, setIsMatrixOpen] = useState<boolean>(false);
    const [onlyRandomized, setOnlyRandomized] = useState<boolean>(true);
    const [searchText, setSearchText] = useState<string>('');

    const texts = TEXTS[source];
    const { columns, rows, progress, timeline } = model;
    const optionalVisits = columns.length - progress.requiredVisits;
    const outsideExpected = progress.doneTotal - progress.doneExpected;

    const visibleRows = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        return rows.filter(
            (row) =>
                (!onlyRandomized || row.stage === 'randomized') &&
                (!query || (row.numberInProtocol ?? '').toLowerCase().includes(query))
        );
    }, [rows, onlyRandomized, searchText]);

    const timelineOption = useMemo(() => {
        if (timeline.points.length === 0) return null;
        return {
            tooltip: {
                trigger: 'axis',
                formatter: (params: any[]) => {
                    const point = timeline.points[params[0].dataIndex];
                    return `<b>${Functions.formatMonth(point.month)}</b><br/>` +
                        `${texts.timelineBars}: <b>${point.count}</b><br/>` +
                        `Acumulado: <b>${point.cumulative}</b>`;
                }
            },
            legend: { data: [texts.timelineBars, 'Acumulado'], bottom: 0, textStyle: { fontSize: 11 } },
            grid: { left: 12, right: 12, top: 16, bottom: 36, containLabel: true },
            xAxis: {
                type: 'category',
                data: timeline.points.map((point) => Functions.formatMonth(point.month)),
                axisLabel: { fontSize: 11 }
            },
            yAxis: [
                { type: 'value', minInterval: 1, name: 'Mes', nameTextStyle: { fontSize: 10 }, splitLine: { lineStyle: { color: '#f0efee' } } },
                { type: 'value', minInterval: 1, name: 'Acumulado', nameTextStyle: { fontSize: 10 }, splitLine: { show: false } }
            ],
            series: [
                {
                    name: texts.timelineBars,
                    type: 'bar',
                    barMaxWidth: 28,
                    itemStyle: { color: '#0078d4', borderRadius: [4, 4, 0, 0] },
                    data: timeline.points.map((point) => point.count)
                },
                {
                    name: 'Acumulado',
                    type: 'line',
                    yAxisIndex: 1,
                    smooth: true,
                    symbolSize: 6,
                    itemStyle: { color: '#107c10' },
                    data: timeline.points.map((point) => point.cumulative)
                }
            ]
        };
    }, [timeline, texts]);

    return (
        <div className="cs-card">
            <div className="cs-card__header cs-card__header--actions">
                <div className="cs-card__header-text">
                    <div className="cs-card__title">Ejecución de visitas</div>
                    <div className="cs-card__desc">{texts.description}</div>
                </div>
                <ExcelExportButton
                    disabled={columns.length === 0}
                    onExport={() =>
                        downloadWorkbook(buildFileName(fileBase ?? 'ejecucion-de-visitas'), visitExecutionSheets(model), {
                            title: 'Ejecución de visitas',
                            scope: exportScope,
                            note: source === 'contract'
                                ? 'Una visita cuenta como realizada cuando el paciente tiene al menos un consumo cargado en ella.'
                                : 'Una visita cuenta como realizada cuando está Completada, Modificada o Auto completada.'
                        })
                    }
                />
            </div>

            {isLoading && columns.length === 0 ? (
                <div className="cs-loading cs-loading--compact">
                    <Spinner size={SpinnerSize.small} />
                    <span>Cargando plan de visitas…</span>
                </div>
            ) : columns.length === 0 ? (
                <div className="cs-empty">
                    <CalendarCheckmarkRegular className="cs-empty__icon" />
                    <span>{texts.empty}</span>
                </div>
            ) : (
                <>
                    <div className="cs-kpi-grid">
                        <div className="cs-kpi-cell">
                            <div className="cs-kpi-cell__icon cs-kpi-cell__icon--green"><ArrowTrendingRegular /></div>
                            <div className="cs-kpi-cell__value">
                                {progress.doneExpected} <span className="ve-kpi__of">de {progress.expected}</span>
                            </div>
                            <div className="cs-kpi-cell__label">Visitas esperadas realizadas</div>
                            <div className="cs-kpi-cell__desc">
                                {texts.expectedDesc(progress.requiredVisits, progress.randomizedPatients)}
                            </div>
                            <div className="cs-kpi-cell__sub">{progress.percentage}% de avance</div>
                        </div>
                        <div className="cs-kpi-cell">
                            <div className="cs-kpi-cell__icon cs-kpi-cell__icon--blue"><TaskListSquareLtrRegular /></div>
                            <div className="cs-kpi-cell__value">{columns.length}</div>
                            <div className="cs-kpi-cell__label">Visitas por paciente</div>
                            <div className="cs-kpi-cell__desc">{texts.planDesc}</div>
                            {optionalVisits > 0 && (
                                <div className="cs-kpi-cell__sub">
                                    {progress.requiredVisits} obligatorias · {optionalVisits} opcionales
                                </div>
                            )}
                        </div>
                        <div className="cs-kpi-cell">
                            <div className="cs-kpi-cell__icon cs-kpi-cell__icon--blue"><PeopleRegular /></div>
                            <div className="cs-kpi-cell__value">{progress.randomizedPatients}</div>
                            <div className="cs-kpi-cell__label">Pacientes randomizados</div>
                            <div className="cs-kpi-cell__desc">Los únicos de los que se esperan visitas del plan</div>
                        </div>
                        <div className="cs-kpi-cell">
                            <div className="cs-kpi-cell__icon cs-kpi-cell__icon--gray"><CalendarCheckmarkRegular /></div>
                            <div className="cs-kpi-cell__value">{outsideExpected}</div>
                            <div className="cs-kpi-cell__label">Otras visitas realizadas</div>
                            <div className="cs-kpi-cell__desc">
                                {source === 'contract'
                                    ? 'Visitas opcionales o de pacientes no randomizados: realizadas, pero fuera de lo esperado'
                                    : 'Visitas de pacientes no randomizados: realizadas, pero fuera de lo esperado'}
                            </div>
                            {model.extraVisits > 0 && (
                                <div className="cs-kpi-cell__sub">
                                    Además, {model.extraVisits} fuera del plan del protocolo
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="cs-card__body">
                        <div className="ve-progress" aria-label={`${progress.percentage}% de avance`}>
                            <div className="ve-progress__bar">
                                <div
                                    className="ve-progress__fill"
                                    style={{ width: `${Math.min(progress.percentage, 100)}%` }}
                                />
                            </div>
                            <span className="ve-progress__label">
                                {progress.doneExpected} de {progress.expected} visitas esperadas · {progress.percentage}%
                            </span>
                        </div>

                        <div className="cs-cf-chart-title cs-block--divided">Línea de tiempo</div>
                        <div className="cs-cf-chart-desc">Barras: {texts.timelineBars.toLowerCase()}. Línea: acumulado.</div>
                        {timelineOption ? (
                            <ReactECharts option={timelineOption} style={{ height: 280 }} opts={{ renderer: 'svg' }} />
                        ) : (
                            <div className="cs-empty cs-empty--compact">
                                <span>Todavía no hay visitas realizadas con fecha.</span>
                            </div>
                        )}
                        {timeline.undated > 0 && (
                            <div className="ve-note">
                                {timeline.undated} {timeline.undated === 1 ? 'visita realizada no tiene' : 'visitas realizadas no tienen'} fecha
                                y no figura{timeline.undated === 1 ? '' : 'n'} en la línea de tiempo.
                            </div>
                        )}

                        <div className="pss-detail">
                            <button
                                type="button"
                                className="pss-detail__toggle"
                                aria-expanded={isMatrixOpen}
                                onClick={() => setIsMatrixOpen(!isMatrixOpen)}
                            >
                                <span
                                    className={'pss-detail__chevron' + (isMatrixOpen ? ' pss-detail__chevron--open' : '')}
                                    aria-hidden="true"
                                >
                                    ›
                                </span>
                                {isMatrixOpen ? 'Ocultar detalle por paciente' : 'Ver detalle por paciente'}
                            </button>

                            {isMatrixOpen && (
                                <>
                                    <div className="ve-matrix__toolbar">
                                        <SearchBox
                                            className="ve-matrix__search"
                                            placeholder="Buscar por N° en protocolo"
                                            underlined={false}
                                            value={searchText}
                                            onChange={(_, newValue) => setSearchText(newValue || '')}
                                            onClear={() => setSearchText('')}
                                        />
                                        <Toggle
                                            inlineLabel
                                            label="Sólo randomizados"
                                            checked={onlyRandomized}
                                            onChange={(_, checked) => setOnlyRandomized(!!checked)}
                                        />
                                        <span className="pss-detail__count">
                                            {visibleRows.length} de {rows.length} pacientes
                                        </span>
                                    </div>

                                    {source === 'emr' && (
                                        <div className="ve-legend">
                                            {(Object.keys(CELL_SYMBOL) as Functions.VisitCellState[]).map((state) => (
                                                <span key={state} className={`ve-legend__item ve-matrix__cell--${state}`}>
                                                    <span aria-hidden="true">{CELL_SYMBOL[state]}</span> {CELL_TITLE[state]}
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    {visibleRows.length === 0 ? (
                                        <div className="cs-empty cs-empty--compact">
                                            <span>Ningún paciente coincide con el filtro.</span>
                                        </div>
                                    ) : (
                                        <div className="ve-matrix">
                                            <table className="ve-matrix__table">
                                                <thead>
                                                    <tr>
                                                        <th className="ve-matrix__sticky">Paciente</th>
                                                        {columns.map((column) => (
                                                            <th
                                                                key={column.key}
                                                                className={column.isOptional ? 've-matrix__optional' : undefined}
                                                                title={column.isOptional ? `${column.name} (opcional)` : column.name}
                                                            >
                                                                {column.name}
                                                                {column.isOptional && <span className="ve-matrix__opt-tag">opc.</span>}
                                                            </th>
                                                        ))}
                                                        <th>{source === 'contract' ? 'Obligatorias' : 'Realizadas'}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {visibleRows.map((row) => (
                                                        <tr key={row.numberInProtocol}>
                                                            <td className="ve-matrix__sticky">
                                                                <div className="ve-matrix__patient">{row.numberInProtocol || '—'}</div>
                                                                <span className={`pss-stage-tag pss-stage-tag--${row.stage}`}>
                                                                    {statusLabel(row.status, row.statusDescription)}
                                                                </span>
                                                            </td>
                                                            {columns.map((column) => {
                                                                const cell = row.cells.get(column.key);
                                                                const date = Functions.formatShortDate(cell?.time);
                                                                return (
                                                                    <td
                                                                        key={column.key}
                                                                        className={
                                                                            've-matrix__cell' +
                                                                            (cell ? ` ve-matrix__cell--${cell.state}` : '') +
                                                                            (column.isOptional ? ' ve-matrix__optional' : '')
                                                                        }
                                                                        title={
                                                                            cell
                                                                                ? `${column.name}: ${CELL_TITLE[cell.state]}${date ? ' el ' + date : ' (sin fecha)'}`
                                                                                : `${column.name}: pendiente`
                                                                        }
                                                                    >
                                                                        {cell ? (
                                                                            <>
                                                                                <span className="ve-matrix__check" aria-hidden="true">
                                                                                    {CELL_SYMBOL[cell.state]}
                                                                                </span>
                                                                                <span className="ve-matrix__date">{date || 's/f'}</span>
                                                                            </>
                                                                        ) : (
                                                                            <span className="ve-matrix__pending" aria-label="pendiente">·</span>
                                                                        )}
                                                                    </td>
                                                                );
                                                            })}
                                                            <td className="ve-matrix__total">
                                                                {row.doneRequired}/{progress.requiredVisits}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                    {onlyRandomized && (
                                        <div className="ve-note">
                                            Mostrando sólo pacientes en etapa {STAGE_LABEL.randomized.toLowerCase()}. Desactivá el filtro para ver también selección y fallas de selección.
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default VisitExecutionComponent;
